import type { DetailedEvent } from '@aether/services/sanity-service'
import { ROUTE_BASE_EVENTS } from '@aether/configs'
import {
  Button,
  Container,
  Link,
  Media,
  PortableText,
  StickyWrapper,
  useShowOnScroll,
  Dropdown,
} from '@aether/components'
import { styled, useMediaQuery } from '@aether/styles'
import { formatDateLong } from '@aether/components'
import { useState, useEffect } from 'react'
import { ArrowLeft } from '@aether/icons'
import { useTranslation } from 'react-i18next'
import { useCustomerContext } from '@aether/account/utils-customer-context'
import { EventButton } from '@aether/components'
import { PageBuilder } from '@aether/page-builder'
import {
  type CalendarProvider,
  generateCalendarUrlForEvent,
  getIsDateInPDTInPast,
  useIsIosDevice,
} from '@aether/utils'

const BUTTON_MAX_WIDTH = '350px'

const MediaWrap = styled('div', {
  position: 'relative',
  width: '100%',
})

const HeroContainer = styled('div', {
  position: 'relative',
  width: '100%',
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: '1fr',

  '& > *': {
    gridColumn: 1,
    gridRow: 1,
  },

  '& > *:first-child': {
    display: 'block',
    '@lg': {
      display: 'none',
    },
  },

  '& > *:last-child': {
    display: 'none',
    '@lg': {
      display: 'block',
    },
  },
})

const MediaWrapper = styled('div', {
  position: 'relative',
  width: '100%',
  height: '100%',

  '&::after': {
    content: '',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(0deg, $black_light, $black_light)',
    opacity: 0,
  },

  variants: {
    isArchived: {
      true: {
        '&::after': {
          opacity: 1,
        },
        '& img': {
          filter: 'grayscale(100%)',
        },
      },
    },
  },
})

const BackButtonWrap = styled('div', {
  position: 'absolute',
  top: '$20',
  left: '$16',

  '@lg': {
    top: '$40',
    left: '$64',
  },
})

const Title = styled('h1', {
  $aetherFont: 'heading03',
  marginBottom: '$8',
})

const TitleWrap = styled(Container, {
  paddingTop: '$24',
  display: 'grid',
  gridTemplateColumns: '1fr',

  '@lg': {
    paddingTop: '$48',
    gap: 'calc($72 * 2)',
    gridTemplateColumns: '3fr 2fr',
    alignItems: 'center',
  },
})

const CtaContainer = styled('div', {
  display: 'none',
  '@lg': {
    display: 'inherit',
  },
})

const ContentWrap = styled(Container, {
  paddingTop: '$36',
  paddingBottom: '$144',
  display: 'grid',
  gridTemplateColumns: '1fr',

  '@lg': {
    paddingTop: '$48',
    gridTemplateColumns: '1fr 2fr 2fr',
    gap: '$72',
  },
})

const FirstSection = styled('div', {
  order: 2,
  '@lg': {
    order: 1,
  },
})

const SecondSection = styled('div', {
  order: 1,
  '@lg': {
    order: 2,
  },
})

const ThirdSection = styled('div', {
  order: 3,
})

const ContentSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$36',
  marginBottom: '$36',
})

const SectionTitle = styled('h5', {
  $aetherFont: 'ui05',
  marginBottom: '$20',
  color: '$gray_medium',
})

const SectionContent = styled('p', {
  $aetherFont: 'body02',
  fontSize: 14,
})

const SectionContentRichText = styled(PortableText, {
  fontSize: 14,
  $aetherFont: 'body02',
})

const ActionButtonsWrap = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '$12',

  '@lg': {
    gridTemplateColumns: '1fr 1fr',
  },

  button: {
    maxWidth: BUTTON_MAX_WIDTH,

    '@lg': {
      maxWidth: 'none',
    },
  },

  'div[role="combobox"]': {
    width: '100%',
    maxWidth: BUTTON_MAX_WIDTH,

    '@lg': {
      maxWidth: 'none',
    },
  },
})

const ActionButton = styled(Button, {
  width: '100%',
})

const ViewMapButton = styled(Link, {
  $aetherFont: 'body03',
  textDecoration: 'underline',
  fontSize: 14,
  $focusStyle: 'default',
})

const BackButton = styled(Link, {
  alignItems: 'center',
  gap: '$8',
  padding: '$8 $12',
  borderRadius: '$r1',
  backgroundColor: '$white',
  textTransform: 'uppercase',
  $aetherFont: 'ui02',
  opacity: 0.8,
  transition: 'opacity 0.3s ease',
  $focusStyle: 'default',

  '&:hover': {
    opacity: 1,
  },

  svg: {
    width: '$14',
    height: '$14',
  },
})

interface EventDetailsProps {
  eventData: DetailedEvent
}

export const EventDetails: React.FC<EventDetailsProps> = ({ eventData }) => {
  const isLg = useMediaQuery('lg')
  const [isCopied, setIsCopied] = useState(false)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const isIosDevice = useIsIosDevice()
  const { t } = useTranslation('eventDetails')
  const { userRole } = useCustomerContext()
  const isVip = userRole === 'vip'
  const isRsvpValid = !getIsDateInPDTInPast(eventData.rsvpEndDate)
  // Don't render sticky CTA on server to avoid layout change
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const isArchivedEvent = eventData.isEventEnded

  const isStickyCtaVisible = useShowOnScroll({
    hideAfter: 'footer',
    showAfterOffsetType: 'bottomInView',
    showAfterOffset: 100,
    hideAfterOffsetType: 'topInView',
  })

  const generateDate = (startDate?: string, endDate?: string) => {
    if (startDate && endDate) {
      return `${formatDateLong(startDate)} - ${formatDateLong(endDate)}`
    }
    return startDate ? formatDateLong(startDate) : ''
  }

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      // fail silently
    }
  }

  const handleCalendarClick = (provider: CalendarProvider) => {
    if (!eventData.startDate || !eventData.title) return
    const { startDate, endDate, title } = eventData

    const url = generateCalendarUrlForEvent(
      {
        startDate,
        endDate,
        title,
        locationShort: eventData.locationShort ?? '',
      },
      provider,
    )
    if (provider === 'apple') {
      // For Apple Calendar, we need to create a data URL and trigger download
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${eventData.title}.ics`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      window.open(url, '_blank')
    }
    setIsCalendarOpen(false)
  }

  useEffect(() => {
    return () => {
      if (isCopied) {
        setIsCopied(false)
      }
    }
  }, [isCopied])

  return (
    <Container spaceSize="none">
      <MediaWrap>
        <HeroContainer>
          {/* Mobile */}
          <MediaWrapper isArchived={isArchivedEvent}>
            {eventData.media && (
              <Media
                mediaPayload={eventData.media.mediaPayload}
                hardcropRatio="square"
              />
            )}
          </MediaWrapper>
          {/* Desktop */}
          <MediaWrapper isArchived={isArchivedEvent}>
            {eventData.media && (
              <Media
                mediaPayload={eventData.media.mediaPayload}
                hardcropRatio="landscape3"
              />
            )}
          </MediaWrapper>
        </HeroContainer>
        <BackButtonWrap>
          <BackButton appearance="flex" href={ROUTE_BASE_EVENTS}>
            <ArrowLeft /> {t('allEvents')}
          </BackButton>
        </BackButtonWrap>
      </MediaWrap>
      <TitleWrap>
        <Title>{eventData.title ?? ''}</Title>
        {!isArchivedEvent && (
          <CtaContainer>
            <EventButton
              eventData={{
                isVipEvent: eventData.isVipEvent ?? false,
                isRsvpEnded: !isRsvpValid,
                primaryCta: {
                  label: eventData.primaryCta?.label,
                  link: eventData.primaryCta?.link,
                  moreInformation: eventData.primaryCta?.moreInformation,
                },
              }}
              isVip={isVip}
            />
          </CtaContainer>
        )}
      </TitleWrap>
      <ContentWrap>
        <FirstSection>
          <ContentSection>
            <div>
              <SectionTitle>{t('date')}</SectionTitle>
              <SectionContent>
                {eventData.startDate
                  ? generateDate(eventData.startDate, eventData.endDate)
                  : ''}
              </SectionContent>
            </div>
            <div>
              <SectionTitle>{t('location')}</SectionTitle>
              <SectionContentRichText
                fontSize="S"
                withSpacing={false}
                value={eventData.locationLong}
              />
              {eventData.locationLink && (
                <ViewMapButton
                  appearance="defaultLink"
                  as="a"
                  {...eventData.locationLink}
                >
                  {t('viewMap')}
                </ViewMapButton>
              )}
            </div>
          </ContentSection>
        </FirstSection>

        <SecondSection>
          {eventData.descriptionSection?.length && (
            <ContentSection>
              {eventData.descriptionSection!.map((section) => (
                <div key={section.title}>
                  <SectionTitle>{section.title}</SectionTitle>
                  <SectionContentRichText
                    fontSize="S"
                    withSpacing={false}
                    value={section.content}
                  />
                </div>
              ))}
            </ContentSection>
          )}
        </SecondSection>

        <ThirdSection>
          {eventData.infoSection?.length && (
            <ContentSection>
              {eventData.infoSection!.map((section) => (
                <div key={section.title}>
                  <SectionTitle>{section.title}</SectionTitle>
                  <SectionContentRichText
                    fontSize="S"
                    withSpacing={false}
                    value={section.content}
                  />
                </div>
              ))}
            </ContentSection>
          )}
          <ActionButtonsWrap>
            <ActionButton appearance="badgeOutlineBig" onClick={handleShare}>
              {isCopied ? t('linkCopied') : t('share')}
            </ActionButton>
            <Dropdown
              isOpen={isCalendarOpen}
              onOpenChange={setIsCalendarOpen}
              trigger={
                <ActionButton appearance="badgeOutlineBig">
                  {t('addToCalendar')}
                </ActionButton>
              }
              items={[
                {
                  id: 'google',
                  content: 'Google',
                  onClick: () => handleCalendarClick('google'),
                },
                {
                  id: 'outlook',
                  content: 'Outlook',
                  onClick: () => handleCalendarClick('outlook'),
                },
                ...(isIosDevice
                  ? [
                      {
                        id: 'apple',
                        content: 'Apple',
                        onClick: () => handleCalendarClick('apple'),
                      },
                    ]
                  : []),
              ]}
            />
          </ActionButtonsWrap>
        </ThirdSection>
      </ContentWrap>

      {eventData.pageBuilder?.map((section, index) => (
        <PageBuilder
          key={`${section._type}-${index}`}
          section={{
            ...section,
            priority: index === 0,
          }}
        />
      ))}

      {!isArchivedEvent && (
        <StickyWrapper visible={isStickyCtaVisible && isClient && !isLg}>
          <EventButton
            eventData={{
              isVipEvent: eventData.isVipEvent ?? false,
              isRsvpEnded: !isRsvpValid,
              primaryCta: {
                label: eventData.primaryCta?.label,
                link: eventData.primaryCta?.link,
                moreInformation: eventData.primaryCta?.moreInformation,
              },
            }}
            isVip={isVip}
          />
        </StickyWrapper>
      )}
    </Container>
  )
}
